import { logger } from "@/lib/logger";

import { runtimeConfigSchema, type RuntimeConfig } from "./schema";

/**
 * Loads and validates /config.json.
 *
 * Every failure mode below is terminal and produces a screen that names the
 * cause. The application never falls back to a default tenant, a default
 * endpoint, or an anonymous identity — issue #4 requires that missing runtime
 * configuration fails closed.
 *
 * This module is also the **enforcement point** for the credential refusal in
 * `./schema` (#45), and that is why it is owned and gated alongside the detector
 * rather than treated as ordinary plumbing. `runtimeConfigSchema` is `strict()` and
 * puts every string in the file through `credentialShapedValue`; none of that runs
 * unless `loadRuntimeConfig` invokes it. A change that fetched `/config.json` and
 * skipped the parse — or that swapped `safeParse` for a bare `JSON.parse` — would
 * leave every assertion the browser invariant suite makes about the schema green
 * while removing the runtime control entirely, which is why that suite (under
 * `src/invariants/`) exercises the refusal *through this function* and not only
 * through `safeParse` by hand.
 *
 * What refusing here does not buy: by the time this runs, `/config.json` has already
 * been written into the served directory and fetched over the network. Failing the
 * application closed protects the application; it does not un-disclose a credential
 * that is now downloadable by anyone. `scripts/validate-runtime-config.mjs` applies
 * this same schema before the file is served, which is the only point at which
 * refusing preserves confidentiality.
 */

export const RUNTIME_CONFIG_URL = "/config.json";

export type ConfigFailureKind =
  | "unreachable"
  | "http-error"
  | "malformed-json"
  | "invalid-schema"
  | "production-refused";

export class RuntimeConfigError extends Error {
  readonly kind: ConfigFailureKind;
  /** Field-level detail, one entry per invalid key. Never contains PHI. */
  readonly details: string[];

  constructor(kind: ConfigFailureKind, message: string, details: string[] = []) {
    super(message);
    this.name = "RuntimeConfigError";
    this.kind = kind;
    this.details = details;
  }
}

/**
 * ADR 0001 decision 9: production deploys none of the preview stack and
 * structurally refuses preview candidates. The browser refuses too, so a
 * config file that names production can never produce a usable editor.
 */
function assertNotProduction(payload: unknown): void {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "environment" in payload &&
    (payload as { environment: unknown }).environment === "production"
  ) {
    throw new RuntimeConfigError(
      "production-refused",
      "The Clinician Editor does not run against production.",
      [
        'Runtime configuration declared environment "production".',
        "Preview is non-production by construction (ADR 0001, decision 9).",
      ],
    );
  }
}

export async function loadRuntimeConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<RuntimeConfig> {
  let response: Response;
  try {
    response = await fetchImpl(RUNTIME_CONFIG_URL, {
      cache: "no-store",
      credentials: "omit",
      headers: { accept: "application/json" },
    });
  } catch (cause) {
    logger.error("Runtime configuration could not be fetched", {
      errorName: cause instanceof Error ? cause.name : "unknown",
    });
    throw new RuntimeConfigError(
      "unreachable",
      "Runtime configuration could not be retrieved.",
      [`Request to ${RUNTIME_CONFIG_URL} failed.`],
    );
  }

  if (!response.ok) {
    logger.error("Runtime configuration request failed", {
      status: response.status,
    });
    throw new RuntimeConfigError(
      "http-error",
      "Runtime configuration could not be retrieved.",
      [`${RUNTIME_CONFIG_URL} responded with HTTP ${response.status}.`],
    );
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new RuntimeConfigError(
      "malformed-json",
      "Runtime configuration is not valid JSON.",
      [`${RUNTIME_CONFIG_URL} did not parse as JSON.`],
    );
  }

  assertNotProduction(payload);

  const parsed = runtimeConfigSchema.safeParse(payload);
  if (!parsed.success) {
    const details = parsed.error.issues.map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join(".") : "(root)";
      return `${path}: ${issue.message}`;
    });
    logger.error("Runtime configuration failed validation", {
      count: details.length,
    });
    throw new RuntimeConfigError(
      "invalid-schema",
      "Runtime configuration is not valid.",
      details,
    );
  }

  // No tenant is logged here: runtime configuration carries none as of v3
  // (#24). The tenant belongs to the session, and the session is resolved by
  // the Editor Service well after this point.
  logger.info("Runtime configuration loaded", {
    schemaVersion: parsed.data.schemaVersion,
    environment: parsed.data.environment,
  });

  return parsed.data;
}
